/**
 * useExistingDebtHandlers - Hook for Existing Debt page handlers
 *
 * Extracts handler logic from ExistingDebtPage to improve separation of concerns
 * and make handlers reusable/testable.
 *
 * Responsibilities:
 * - Save form data (onChange handlers)
 * - Automatically build calculated data when inputs are complete (for review during setup)
 * - Provide save point handler (handleSaveAndContinue) for pages to trigger building
 * - Handle reverse amortization (special case for immediate user feedback)
 *
 * Usage:
 * ```tsx
 * const handlers = useExistingDebtHandlers(projectId);
 * handlers.handleProjectChange(updatedProject, allProjects, activeTab);
 *
 * // At save point (Continue button):
 * const result = handlers.handleSaveAndContinue();
 * ```
 */

import { useCallback, useEffect, useRef } from 'react';

import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import {
  convertToEngineFormat,
  createEmptyExistingDebtInputForm,
} from '~/builders/existing-debt/ExistingDebtInputs';
import { useProjectStore } from '~/hooks/useProjectStore';
import { BuildService } from '~/services/build-service';
import type { ExistingDebtInputForm } from '~/types';

// Debounce delay for expensive build operations (ms)
const BUILD_DEBOUNCE_MS = 500;

/**
 * Check if a bond series is complete (at least one of the last two principal payments is > 0)
 * Incomplete only if BOTH last two principal payments are zero
 */
export const isBondSeriesComplete = (project: ExistingDebtInputForm): boolean => {
  if (!project.payments || project.payments.length < 2) {
    return false;
  }

  // Get the last two payments
  const sortedPayments = [...project.payments].sort((a, b) =>
    a.paymentDate.localeCompare(b.paymentDate)
  );
  const lastTwoPayments = sortedPayments.slice(-2);

  // Check if at least one has principal > 0
  // Complete if ONE or BOTH are > 0, incomplete only if BOTH are zero
  return lastTwoPayments.some((payment) => {
    const principalStr = payment.principalPayment || '';
    const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;
    return principal > 0;
  });
};

export const useExistingDebtHandlers = (projectId: string) => {
  const saveExistingDebtInputsForm = useProjectStore((s) => s.saveExistingDebtInputsForm);
  const updateProjectMetadata = useProjectStore((s) => s.updateProjectMetadata);

  // Get form inputs and dependencies from store for reactive building
  const existingDebtInputs = useProjectStore((s) => s.projects[projectId]?.existingDebtInputs);
  const dateRecords = useProjectStore((s) => s.projects[projectId]?.dateRecords);

  // Debounce timer for expensive build operations
  const debtBuildTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Process and save debt projects (converts form to engine format and processes)
   * @deprecated Internal use only - kept for backwards compatibility
   * Use BuildService.buildExistingDebtProjects() instead
   */
  const processAndSaveDebtProjects = useCallback(
    (projects: ExistingDebtInputForm[]) => {
      const store = useProjectStore.getState();
      const dateRecords = store.projects[projectId]?.dateRecords;
      const clearExistingDebtProjects = store.clearExistingDebtProjects;
      const saveExistingDebtProjects = store.saveExistingDebtProjects;

      try {
        if (!projects || projects.length === 0) {
          clearExistingDebtProjects(projectId);
          return;
        }

        // Convert each form to engine format and process
        // Filter to ensure we only process valid projects
        const processed = projects
          .filter((form) => form.id?.trim() && form.name?.trim() && form.payments?.length > 0)
          .map((form) => {
            const engineFormat = convertToEngineFormat(form);
            return ExistingDebtBuilder.processDebtProject(engineFormat, dateRecords || []);
          });

        // Save processed data (for drawer display and cumulative debt)
        // This happens even with a single project to ensure cumulative debt is available
        if (processed.length > 0) {
          saveExistingDebtProjects(projectId, processed);
        } else {
          clearExistingDebtProjects(projectId);
        }
      } catch (error) {
        console.error('Error processing debt projects:', error);
        // Clear on error (cascade pattern)
        clearExistingDebtProjects(projectId);
      }
    },
    [projectId]
  );

  /**
   * Handle project change - saves form inputs and triggers building if valid
   */
  const handleProjectChange = useCallback(
    (
      updatedProject: ExistingDebtInputForm,
      allProjects: ExistingDebtInputForm[],
      activeTab: number
    ) => {
      // Update the specific project in the array
      const updated = [...allProjects];
      updated[activeTab] = updatedProject;

      // Save form inputs
      saveExistingDebtInputsForm(projectId, updated);
      // Building will happen automatically via useEffect when inputs are valid
    },
    [projectId, saveExistingDebtInputsForm]
  );

  // Auto-build ExistingDebtProjects when inputs become valid (requires dateRecords, debounced)
  useEffect(() => {
    // Clear any pending build timer when inputs change
    if (debtBuildTimerRef.current) {
      clearTimeout(debtBuildTimerRef.current);
      debtBuildTimerRef.current = null;
    }

    if (!existingDebtInputs || existingDebtInputs.length === 0 || !dateRecords) return;

    // Check if we have any complete projects
    const completeProjects = existingDebtInputs.filter((form) => {
      if (!form.id?.trim() || !form.name?.trim() || !form.payments || form.payments.length === 0) {
        return false;
      }
      return isBondSeriesComplete(form);
    });

    if (completeProjects.length > 0) {
      // Debounce the build operation to avoid running on every keystroke
      debtBuildTimerRef.current = setTimeout(() => {
        BuildService.buildExistingDebtProjects(projectId);
        debtBuildTimerRef.current = null;
      }, BUILD_DEBOUNCE_MS);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debtBuildTimerRef.current) {
        clearTimeout(debtBuildTimerRef.current);
        debtBuildTimerRef.current = null;
      }
    };
  }, [projectId, existingDebtInputs, dateRecords]);

  /**
   * Handle decrease count - removes one project
   */
  const handleDecreaseCount = useCallback(
    (currentProjects: ExistingDebtInputForm[], currentActiveTab: number) => {
      if (currentProjects.length <= 1) {
        return { updated: currentProjects, newActiveTab: currentActiveTab }; // Keep at least 1 project
      }

      const newCount = currentProjects.length - 1;
      const updated = currentProjects.slice(0, newCount);

      // Adjust active tab if needed
      let newActiveTab = currentActiveTab;
      if (currentActiveTab >= updated.length) {
        newActiveTab = updated.length - 1;
      }

      updateProjectMetadata(projectId, { expectedDebtProjectCount: newCount });
      saveExistingDebtInputsForm(projectId, updated);

      return { updated, newActiveTab };
    },
    [projectId, updateProjectMetadata, saveExistingDebtInputsForm]
  );

  /**
   * Handle increase count - adds one new project
   */
  const handleIncreaseCount = useCallback(
    (currentProjects: ExistingDebtInputForm[]) => {
      const newCount = currentProjects.length + 1;
      const newProject = createEmptyExistingDebtInputForm();
      const updated = [...currentProjects, newProject];

      updateProjectMetadata(projectId, { expectedDebtProjectCount: newCount });
      saveExistingDebtInputsForm(projectId, updated);

      return updated;
    },
    [projectId, updateProjectMetadata, saveExistingDebtInputsForm]
  );

  /**
   * Handle skip - mark as no existing debt and clear data
   */
  const handleSkip = useCallback(() => {
    const store = useProjectStore.getState();
    // Update metadata to indicate no existing debt
    updateProjectMetadata(projectId, { hasExistingDebt: false });
    // Clear any existing debt data
    store.clearExistingDebtProjects(projectId);
    saveExistingDebtInputsForm(projectId, []);
  }, [projectId, updateProjectMetadata, saveExistingDebtInputsForm]);

  /**
   * Handle continue - ensure metadata reflects existing debt
   */
  const handleContinue = useCallback(() => {
    // Ensure metadata reflects existing debt
    updateProjectMetadata(projectId, { hasExistingDebt: true });
  }, [projectId, updateProjectMetadata]);

  /**
   * Handle save and continue - builds cumulative debt at save point
   */
  const handleSaveAndContinue = useCallback(() => {
    // Build cumulative debt projects at save point
    const result = BuildService.buildExistingDebtProjects(projectId);

    return result;
  }, [projectId]);

  return {
    handleProjectChange,
    handleDecreaseCount,
    handleIncreaseCount,
    handleSkip,
    handleContinue,
    handleSaveAndContinue,
    processAndSaveDebtProjects,
  };
};
